import * as Sentry from '@sentry/react'

/**
 * User context interface for Sentry
 */
export interface SentryUserContext {
	id: string | number
	userId: string | number
	name?: string
}

/**
 * Sentry sections enum for error categorization
 */
export enum SentrySections {
	AUTHENTICATION = 'authentication',
	CONTENT = 'content',
	PEOPLE = 'people',
	LOCATION = 'location',
	MY_ORGANIZATION = 'my_organization',
	NOTIFICATION = 'notification',
	CALENDAR = 'calendar'
}

/**
 * Sentry events enum for specific error events
 */
export enum SentryEvents {
	// Authentication events
	SIGNIN_ERROR = 'signin_error',
	REQUEST_OTP_ERROR = 'request_otp_error',
	FORGOT_PASSWORD_ERROR = 'forgot_password_error',

	// Push Notifications events
	CREATE_NOTIFICATION_ERROR = 'create_notification_error',
	DUPLICATE_NOTIFICATION_ERROR = 'duplicate_notification_error',
	GET_NOTIFICATION_LIST_ERROR = 'get_notification_list_error',

	// Content events
	CREATE_CONTENT_ERROR = 'create_content_error',
	DUPLICATE_CONTENT_ERROR = 'duplicate_content_error',
	GET_CONTENTS_LIST_ERROR = 'get_contents_list_error',
	GET_CONTENTS_LIST_PINNED_ERROR = 'get_contents_list_pinned_error',

	// Location events
	CREATE_LOCATION_ERROR = 'create_location_error',
	EDIT_LOCATION_ERROR = 'edit_location_error',
	GET_LOCATIONS_LIST_ERROR = 'get_locations_list_error',
	GET_LOCATION_BY_ID_ERROR = 'get_location_by_id_error',

	// My Organization events
	GET_MY_ORGANIZATION_ERROR = 'get_my_organization_error',
	POST_MY_ORGANIZATION_TAB_ERROR = 'post_my_organization_tab_error',

	// People events
	GET_PEOPLE_TAGS_TABLE_ERROR = 'get_people_tags_table_error',
	GET_PEOPLE_MY_LISTS_TABLE_ERROR = 'get_people_my_lists_table_error',
	GET_PEOPLE_MY_PEOPLE_TABLE_ERROR = 'get_people_my_people_table_error',
	ADD_NEW_TAG_ERROR = 'add_new_tag_error',
	ADD_STATIC_LIST_ERROR = 'add_static_list_error',
	ADD_TAG_DYNAMIC_LIST_ERROR = 'add_tag_dynamic_list_error',
	EDIT_FILTERS_DYNAMIC_LIST_ERROR = 'edit_filters_dynamic_list_error',
	GET_REACH_DATA_TABLE_ERROR = 'get_reach_data_table_error',
	DELETE_STATIC_LIST_ERROR = 'delete_static_list_error',
	DELETE_DYNAMIC_LIST_ERROR = 'delete_dynamic_list_error',
	DELETE_TAGS_ERROR = 'delete_tags_error',

	// Calendar events
	GET_CALENDAR_DATA_ERROR = 'get_calendar_data_error'
}

/**
 * Breadcrumb configuration for Sentry
 */
export interface SentryBreadcrumb {
	category: string
	message: string
	level?: Sentry.SeverityLevel
	data?: Record<string, unknown>
}

/**
 * Options for capturing errors in Sentry
 */
export interface CaptureErrorOptions {
	section: SentrySections | string
	event: SentryEvents | string
	error?: unknown
	breadcrumb?: SentryBreadcrumb
	tags?: Record<string, string>
	extra?: {
		payload?: unknown
		params?: unknown
		response?: unknown
		status?: number
		[key: string]: unknown
	}
}
